/**
 * 开户 / 加开户协议 PDF：H5 预下载缓存，点击时优先带 base64 给 App；失败则走 URL。
 * 身份：docCode 为主键，fullUrl 为下载键。
 */
import {
  fetchAgreementSceneBundle,
  loadAgreementDocsByCodes,
  type AgreementSceneDocItem,
  type GetSceneBundleParams,
  type LoadAgreementDocsParams,
  type LoadAgreementDocsResult,
} from '../api/agreement-doc';

export type AgreementPdfCacheEntry = {
  docCode: string;
  fullUrl: string;
  fileName: string;
  blob: Blob;
  size: number;
};

/** 会话内协议 PDF 缓存合计上限（字节） */
const SESSION_BUDGET = 30 * 1024 * 1024;
/** 接口未给 fileSize 时的单文件预估 */
const DEFAULT_PDF_ESTIMATE = 2 * 1024 * 1024;
/** 预下载并发 */
const PRELOAD_CONCURRENCY = 3;

const cacheByUrl = new Map<string, AgreementPdfCacheEntry>();
const urlByDocCode = new Map<string, string>();
const inflight = new Map<string, Promise<Blob | null>>();

let sessionCacheBytes = 0;

/** 与 agreement-doc-view.resolveAgreementFileUrl 对齐，避免循环依赖 */
function resolveFileUrl(fileUrl?: string) {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('blob:') || raw.startsWith('data:')) {
    return raw;
  }
  const base = String(import.meta.env.VITE_IMG_BASE_URL || '');
  if (!base) return raw;
  if (base.endsWith('/') && raw.startsWith('/')) return `${base}${raw.slice(1)}`;
  if (!base.endsWith('/') && !raw.startsWith('/')) return `${base}/${raw}`;
  return `${base}${raw}`;
}

/** 仅预载 PDF；RICH_TEXT / HTML / TEXT 跳过 */
function isPdfDoc(doc: AgreementSceneDocItem) {
  const type = String(doc.contentType || '')
    .toUpperCase()
    .trim();
  if (type.includes('RICH')) return false;
  if (type.includes('HTML') || type === 'TEXT' || type.includes('PLAIN')) return false;
  if (type === 'PDF' || type.includes('PDF')) return true;
  const name = String(doc.fileName || doc.fileUrl || '').toLowerCase();
  return name.endsWith('.pdf');
}

async function canPreload(docs: AgreementSceneDocItem[]): Promise<boolean> {
  const need = docs.reduce((n, d) => n + (Number(d.fileSize) > 0 ? Number(d.fileSize) : DEFAULT_PDF_ESTIMATE), 0);
  if (sessionCacheBytes + need > SESSION_BUDGET) return false;

  try {
    const est = await navigator.storage?.estimate?.();
    if (est?.quota != null && est.usage != null) {
      const free = est.quota - est.usage;
      // 留 5MB 余量；配额未知或异常时不拦
      if (free < need + 5 * 1024 * 1024) return false;
    }
  } catch {
    // ignore
  }
  return true;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function fetchPdfBlob(fullUrl: string): Promise<Blob | null> {
  const existing = inflight.get(fullUrl);
  if (existing) return existing;

  const p = (async () => {
    try {
      const res = await fetch(fullUrl, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) return null;
      return await res.blob();
    } catch {
      return null;
    } finally {
      inflight.delete(fullUrl);
    }
  })();

  inflight.set(fullUrl, p);
  return p;
}

function putCache(entry: AgreementPdfCacheEntry) {
  const prev = cacheByUrl.get(entry.fullUrl);
  if (prev) sessionCacheBytes -= prev.size;
  cacheByUrl.set(entry.fullUrl, entry);
  if (entry.docCode) urlByDocCode.set(entry.docCode, entry.fullUrl);
  sessionCacheBytes += entry.size;
}

/**
 * 预下载 PDF 列表（空间不足则整批跳过；单文件失败不影响其它）。
 * fire-and-forget 即可；不抛错。
 */
export async function preloadAgreementDocs(
  docs: (AgreementSceneDocItem | null | undefined)[] | null | undefined,
): Promise<void> {
  const pdfs = (docs || []).filter((d): d is AgreementSceneDocItem => !!d?.docCode && isPdfDoc(d));
  if (!pdfs.length) return;

  const pending = pdfs.filter((d) => {
    const fullUrl = resolveFileUrl(d.fileUrl);
    return !!fullUrl && !cacheByUrl.has(fullUrl) && !inflight.has(fullUrl);
  });
  if (!pending.length) return;

  if (!(await canPreload(pending))) return;

  let idx = 0;
  const workers = Array.from({ length: Math.min(PRELOAD_CONCURRENCY, pending.length) }, async () => {
    while (idx < pending.length) {
      const doc = pending[idx++];
      const fullUrl = resolveFileUrl(doc.fileUrl);
      if (!fullUrl || cacheByUrl.has(fullUrl)) continue;

      const blob = await fetchPdfBlob(fullUrl);
      if (!blob) continue;

      // 预载过程中预算被其它批次占满则停止写入
      if (sessionCacheBytes + blob.size > SESSION_BUDGET) continue;

      putCache({
        docCode: doc.docCode,
        fullUrl,
        fileName: doc.fileName || `${doc.docCode}.pdf`,
        blob,
        size: blob.size,
      });
    }
  });
  await Promise.all(workers);
}

/** 按 fullUrl 或 docCode 取缓存 */
export function getCachedAgreementPdf(opts: {
  fullUrl?: string;
  docCode?: string;
}): AgreementPdfCacheEntry | null {
  const url =
    opts.fullUrl ||
    (opts.docCode ? urlByDocCode.get(opts.docCode) : undefined);
  if (!url) return null;
  return cacheByUrl.get(url) ?? null;
}

/** 缓存转 base64；失败返回 null（调用方走 URL）。若该 url 正在预载则先等预载结束。 */
export async function getCachedAgreementPdfBase64(opts: {
  fullUrl?: string;
  docCode?: string;
}): Promise<{ base64: string; fileName: string; docCode: string; fullUrl: string } | null> {
  let url =
    opts.fullUrl ||
    (opts.docCode ? urlByDocCode.get(opts.docCode) : undefined);

  if (url) {
    const pending = inflight.get(url);
    if (pending) await pending;
  }

  const entry = getCachedAgreementPdf({ fullUrl: url, docCode: opts.docCode });
  if (!entry) return null;
  try {
    const base64 = await blobToBase64(entry.blob);
    return {
      base64,
      fileName: entry.fileName,
      docCode: entry.docCode,
      fullUrl: entry.fullUrl,
    };
  } catch {
    return null;
  }
}

/** 开户 / 加开户流程结束时清空，释放内存 */
export function clearAgreementPdfCache() {
  cacheByUrl.clear();
  urlByDocCode.clear();
  inflight.clear();
  sessionCacheBytes = 0;
}

/** 拉场景协议并自动预载 PDF（开户 / 加开户业务页统一用） */
export async function loadAgreementDocsByCodesWithPreload(
  params: LoadAgreementDocsParams,
): Promise<LoadAgreementDocsResult> {
  const result = await loadAgreementDocsByCodes(params);
  void preloadAgreementDocs(Object.values(result.docMap));
  return result;
}

/** 整包场景协议并自动预载 PDF（如 ConfirmAndSign） */
export async function fetchAgreementSceneBundleWithPreload(params: GetSceneBundleParams) {
  const bundle = await fetchAgreementSceneBundle(params);
  void preloadAgreementDocs(Array.isArray(bundle?.docs) ? bundle.docs : []);
  return bundle;
}
